import { z } from "zod";
import { Variables } from "@/lib/GraphQLCodegen/fetcher";
import type { TableId } from "@/app/Common/Utils/table-id";

import { GetQueryVariablesFn } from "@/lib/Components/DataTable/DataTable";

// Zod schemas for validation
const SortingSchema = z.array(
  z.object({
    desc: z.boolean(),
    id: z.string(),
  }),
);

const PageSizeSchema = z.number().int().min(1).max(100);

/** Page is 1-based, matching the graphql pagination the tables request. */
type TableStateOverrides = {
  page?: number;
  search?: string;
};

export function getTableState<TColumn extends string>(
  tableId: string,
  defaultSorting: { desc: boolean; id: TColumn }[] = [],
  { page = 1, search = "" }: TableStateOverrides = {},
): {
  search: string;
  pagination: {
    pageIndex: number;
    pageSize: number;
  };
  sorting: { desc: boolean; id: TColumn }[];
} {
  // Parse and validate sorting from localStorage. An absent key means the user
  // has never sorted this table, so it must fall back to the same default the
  // DataTable uses — otherwise the preloaded query key differs from the
  // component's and the warm cache is missed (double fetch, different order).
  let sorting = defaultSorting;
  try {
    const rawSorting = localStorage.getItem(`table-${tableId}-sorting`);
    if (rawSorting !== null) {
      const validatedSorting = SortingSchema.parse(JSON.parse(rawSorting));
      sorting = validatedSorting as { desc: boolean; id: TColumn }[];
    }
  } catch (error) {
    console.warn(
      `Invalid sorting data for table ${tableId}, using default:`,
      error,
    );
    sorting = defaultSorting;
  }

  // Parse and validate pageSize from localStorage
  let pageSize = 10;
  try {
    const pageSizeData = JSON.parse(
      localStorage.getItem(`table-${tableId}-page-size`) || "10",
    );
    pageSize = PageSizeSchema.parse(pageSizeData);
  } catch (error) {
    console.warn(
      `Invalid page size for table ${tableId}, using default:`,
      error,
    );
    pageSize = 10;
  }

  return {
    search,
    pagination: {
      pageIndex: page,
      pageSize,
    },
    sorting,
  };
}

// Create query variables using table state and a query variables function
export function createQueryVariables<
  TVariables extends Variables | undefined,
  TColumn extends string,
>(
  tableId: TableId,
  getQueryVariablesFn: GetQueryVariablesFn<TVariables, TColumn>,
  defaultSorting: { desc: boolean; id: TColumn }[] = [],
  overrides: TableStateOverrides = {},
) {
  const tableState = getTableState<TColumn>(tableId, defaultSorting, overrides);
  return getQueryVariablesFn(tableState);
}
